import { z } from 'zod';

import { RoleSchema } from '../common/enums';
import { Email, GSTIN, Id, Password, PhoneIN, PlainText } from '../common/primitives';

/* ----------------------------------- requests ----------------------------------- */

/**
 * Everything a self-service sign-up needs — but submitting it no longer creates
 * an account.
 *
 * `POST /auth/register` starts a phone verification: the details are held as a
 * draft, a code goes to `phone`, and the `User` row is written only when
 * `POST /auth/register/verify` is given that code. The response is therefore
 * `RegisterStartResponse`, not a session.
 */
export const RegisterRequest = z
  .object({
    name: PlainText(120, 2),
    email: Email,
    phone: PhoneIN,
    password: Password,
    /** Self-service registration is limited to these roles; staff/delivery/POS are provisioned by admins. */
    role: z.enum(['CUSTOMER', 'VENDOR', 'REFERRAL_AGENT']).default('CUSTOMER'),
    referralCode: z.string().trim().max(32).optional(),
    acceptTerms: z.literal(true),
  })
  .strict();
export type RegisterRequest = z.infer<typeof RegisterRequest>;
/** `role` carries a schema default, so callers may omit it on the wire. */
export type RegisterInput = z.input<typeof RegisterRequest>;

export const RegisterVendorRequest = z
  .object({
    name: PlainText(120, 2),
    email: Email,
    phone: PhoneIN,
    password: Password,
    storeName: PlainText(120, 2),
    businessType: PlainText(120, 2),
    gstin: GSTIN.optional(),
    city: PlainText(120, 2),
    state: PlainText(120, 2),
  })
  .strict();
export type RegisterVendorRequest = z.infer<typeof RegisterVendorRequest>;

/**
 * What starting a sign-up returns: where the code went and how long it lives.
 *
 * `deliveredTo` is masked (`+91••••••3253`) — it confirms the number the caller
 * typed without echoing a full phone number back over the wire.
 *
 * There is deliberately no enumeration hedging here the way `/password/forgot`
 * has it. Registration already answers "this email/phone is taken" with a 409 by
 * necessity, so pretending not to know would protect nothing.
 */
export const RegisterStartResponse = z.object({
  sent: z.literal(true),
  channel: z.literal('SMS'),
  deliveredTo: z.string(),
  expiresInMinutes: z.number().int(),
});
export type RegisterStartResponse = z.infer<typeof RegisterStartResponse>;

/**
 * Finish a sign-up: prove the phone, and only then get an account.
 *
 * The draft is keyed by phone, so this is all the server needs — which role was
 * being registered, and every other detail, comes from the draft rather than
 * from the client, where it could be swapped between the two steps.
 */
export const RegisterVerifyRequest = z
  .object({
    phone: PhoneIN,
    code: z
      .string()
      .trim()
      .regex(/^\d{6}$/, 'Enter the 6-digit code'),
  })
  .strict();
export type RegisterVerifyRequest = z.infer<typeof RegisterVerifyRequest>;

/** Send a fresh code for a sign-up already in progress. */
export const RegisterResendRequest = z.object({ phone: PhoneIN }).strict();
export type RegisterResendRequest = z.infer<typeof RegisterResendRequest>;

// ── Seller sign-up, phone first ─────────────────────────────────────────────
//
// The seller app proves the phone straight after the personal details, before
// asking for anything about the business:
//
//   1. `/vendor/register/start`        personal details → draft + SMS code
//   2. `/vendor/register/verify-phone` the code → a short-lived registration token
//   3. `/vendor/register/complete`     token + business details → account + session
//
// Nothing is created until step 3, exactly as with `/register/verify`; the token
// only proves that step 2 passed for *this* draft. `/register/resend` sends a
// fresh code during step 2.

export const VendorRegisterStartRequest = z
  .object({
    name: PlainText(120, 2),
    email: Email,
    phone: PhoneIN,
    password: Password,
  })
  .strict();
export type VendorRegisterStartRequest = z.infer<typeof VendorRegisterStartRequest>;

export const VendorRegisterVerifyPhoneRequest = RegisterVerifyRequest;
export type VendorRegisterVerifyPhoneRequest = RegisterVerifyRequest;

export const VendorRegisterVerifyPhoneResponse = z.object({
  /** Hand back to `/vendor/register/complete`. Not a session. */
  registrationToken: z.string(),
  /** How long the business details may take before the sign-up must restart. */
  expiresInMinutes: z.number().int(),
});
export type VendorRegisterVerifyPhoneResponse = z.infer<typeof VendorRegisterVerifyPhoneResponse>;

export const VendorRegisterCompleteRequest = z
  .object({
    registrationToken: z.string().min(20).max(4096),
    storeName: PlainText(120, 2),
    businessType: PlainText(120, 2),
    gstin: GSTIN.optional(),
    city: PlainText(120, 2),
    state: PlainText(120, 2),
  })
  .strict();
export type VendorRegisterCompleteRequest = z.infer<typeof VendorRegisterCompleteRequest>;

export const LoginRequest = z
  .object({
    identifier: z.union([Email, PhoneIN]),
    password: z.string().min(1).max(128),
  })
  .strict();
export type LoginRequest = z.infer<typeof LoginRequest>;

export const OtpRequestRequest = z
  .object({
    channel: z.enum(['SMS', 'EMAIL', 'WHATSAPP']),
    identifier: z.union([Email, PhoneIN]),
    purpose: z.enum(['LOGIN', 'VERIFY_PHONE', 'VERIFY_EMAIL', 'RESET_PASSWORD']),
    /**
     * Mirrors `ForgotPasswordRequest`: the seller portal trades enumeration
     * protection for a plain answer across its small, admin-provisioned set of
     * accounts; the storefront does not.
     *
     * Optional rather than defaulted, so omitting it is the enumeration-safe
     * customer path. A `.default()` here would land in the inferred *output*
     * type and force every existing caller to name a scope in order to keep the
     * behaviour it already had — opting into protection should not be work.
     */
    scope: z.enum(['CUSTOMER', 'VENDOR']).optional(),
  })
  .strict();
export type OtpRequestRequest = z.infer<typeof OtpRequestRequest>;

export const OtpVerifyRequest = z
  .object({
    identifier: z.union([Email, PhoneIN]),
    purpose: z.enum(['LOGIN', 'VERIFY_PHONE', 'VERIFY_EMAIL', 'RESET_PASSWORD']),
    code: z.string().regex(/^\d{4,8}$/),
  })
  .strict();
export type OtpVerifyRequest = z.infer<typeof OtpVerifyRequest>;

// ── Admin panel sign-in: password first, then an SMS code ──────────────────
//
// Two calls, and both are required. `/admin/login/start` checks the password
// and, only if it is right, texts a code to the account's registered mobile
// number and answers with a short-lived
// challenge token. `/admin/login/verify` takes that token plus the code and is
// the only call that issues an admin session. Neither `/login` nor
// `/otp/verify` will sign an admin account in.

export const AdminLoginStartRequest = z
  .object({
    email: Email,
    password: z.string().min(1).max(128),
  })
  .strict();
export type AdminLoginStartRequest = z.infer<typeof AdminLoginStartRequest>;

export const AdminLoginStartResponse = z.object({
  /** Opaque, short-lived; hand it back unchanged to `/admin/login/verify`. */
  challengeToken: z.string(),
  /** Minutes the code (and the token) stay valid. */
  expiresIn: z.number().int().positive(),
  /** Masked mobile number the code went to, for display only. */
  sentTo: z.string(),
});
export type AdminLoginStartResponse = z.infer<typeof AdminLoginStartResponse>;

export const AdminLoginVerifyRequest = z
  .object({
    challengeToken: z.string().min(20),
    code: z.string().regex(/^\d{6}$/),
  })
  .strict();
export type AdminLoginVerifyRequest = z.infer<typeof AdminLoginVerifyRequest>;

// ── Seller portal / app sign-in: password first, then an SMS code ──────────
//
// Identical contract to the admin pair above, against seller accounts. Both
// seller clients used to take a single factor — and different ones (the web
// portal an OTP with no password, the app a password with no OTP) — so either
// half alone opened the account. `/login` and `/otp/verify` now refuse seller
// accounts the same way they refuse admin ones.

export const VendorLoginStartRequest = z
  .object({
    email: Email,
    password: z.string().min(1).max(128),
  })
  .strict();
export type VendorLoginStartRequest = z.infer<typeof VendorLoginStartRequest>;

export const VendorLoginStartResponse = z.object({
  /** Opaque, short-lived; hand it back unchanged to `/vendor/login/verify`. */
  challengeToken: z.string(),
  /** Minutes the code (and the token) stay valid. */
  expiresIn: z.number().int().positive(),
  /** Masked mobile number the code went to, for display only. */
  sentTo: z.string(),
});
export type VendorLoginStartResponse = z.infer<typeof VendorLoginStartResponse>;

export const VendorLoginVerifyRequest = z
  .object({
    challengeToken: z.string().min(20),
    code: z.string().regex(/^\d{6}$/),
  })
  .strict();
export type VendorLoginVerifyRequest = z.infer<typeof VendorLoginVerifyRequest>;

/** Refresh token is normally read from an httpOnly cookie; allow body for non-browser clients. */
export const RefreshRequest = z.object({ refreshToken: z.string().min(10).optional() }).strict();
export type RefreshRequest = z.infer<typeof RefreshRequest>;

/**
 * Start a password reset.
 *
 * `channel` decides where the code goes. It is explicit rather than inferred
 * from the identifier's shape because the two are not the same choice: a user
 * may sign in with their email but only have their phone to hand, and silently
 * picking for them means the code arrives somewhere they cannot read.
 */
export const ForgotPasswordRequest = z
  .object({
    identifier: z.union([Email, PhoneIN]),
    channel: z.enum(['EMAIL', 'SMS']).default('EMAIL'),
    /**
     * Which portal is asking.
     *
     * `CUSTOMER` (the default) answers identically whether or not the account
     * exists, so the storefront cannot be used to test which of a list of email
     * addresses belongs to a customer.
     *
     * `VENDOR` deliberately trades that protection away: sellers are a small,
     * admin-provisioned set who often do not remember which address their store
     * was registered with, and a silent non-answer sends them to support instead
     * of to their inbox. This portal therefore says plainly when no seller
     * account matches. The cost is that seller email addresses become
     * enumerable through this endpoint — accepted knowingly, and bounded by the
     * per-IP auth rate limit.
     */
    scope: z.enum(['CUSTOMER', 'VENDOR']).default('CUSTOMER'),
  })
  .strict();
export type ForgotPasswordRequest = z.infer<typeof ForgotPasswordRequest>;
/**
 * What a caller may send. `channel` carries a schema default, so it is required
 * on the parsed value but optional on the wire — clients that do not offer a
 * choice simply get email.
 */
export type ForgotPasswordInput = z.input<typeof ForgotPasswordRequest>;

/**
 * Whether the reset code could actually be delivered, and where to.
 *
 * `deliveredTo` is masked (`s***m@gmail.com`, `+91••••••3253`) so the response
 * confirms the destination without turning this endpoint into a way to read a
 * stranger's contact details.
 */
export const ForgotPasswordResponse = z.object({
  sent: z.literal(true),
  channel: z.enum(['EMAIL', 'SMS']),
  deliveredTo: z.string().nullable(),
  expiresInMinutes: z.number().int(),
});
export type ForgotPasswordResponse = z.infer<typeof ForgotPasswordResponse>;

/**
 * Check a reset code before asking for the new password, so a mistyped code is
 * caught on the code screen rather than after the customer has chosen and
 * confirmed a password. The code is not spent: the reset itself checks it again.
 */
export const VerifyResetOtpRequest = z
  .object({
    identifier: z.union([Email, PhoneIN]),
    code: z
      .string()
      .trim()
      .regex(/^\d{6}$/, 'Enter the 6-digit code'),
  })
  .strict();
export type VerifyResetOtpRequest = z.infer<typeof VerifyResetOtpRequest>;

/** Finish a reset: prove possession of the code, then set the new password. */
export const ResetPasswordWithOtpRequest = z
  .object({
    identifier: z.union([Email, PhoneIN]),
    code: z
      .string()
      .trim()
      .regex(/^\d{6}$/, 'Enter the 6-digit code'),
    newPassword: Password,
  })
  .strict();
export type ResetPasswordWithOtpRequest = z.infer<typeof ResetPasswordWithOtpRequest>;

/* ----------------------------------- responses ----------------------------------- */

export const AuthSessionUser = z.object({
  id: Id,
  name: z.string(),
  email: Email,
  phone: z.string(),
  roles: z.array(RoleSchema).min(1),
  permissions: z.array(z.string()),
  emailVerified: z.boolean(),
  phoneVerified: z.boolean(),
  /** Public URL of the profile picture, when one has been set. */
  avatarUrl: z.string().url().nullable().optional(),
});
export type AuthSessionUser = z.infer<typeof AuthSessionUser>;

export const AuthTokens = z.object({
  accessToken: z.string(),
  /** Present only for non-cookie clients; browser clients receive it as an httpOnly cookie. */
  refreshToken: z.string().optional(),
  tokenType: z.literal('Bearer').default('Bearer'),
  expiresIn: z.number().int().positive(),
});
export type AuthTokens = z.infer<typeof AuthTokens>;

export const AuthResponse = z.object({ user: AuthSessionUser, tokens: AuthTokens });
export type AuthResponse = z.infer<typeof AuthResponse>;
